/**
 * @file     streak.ts
 * @readme   ./README.md
 * @purpose  Streak calculation logic, Pacific midnight reset with 36h grace period.
 */

import { formatNumericDate } from '@/utils/dateFormat';
import type { UserStreak } from './types';

const GRACE_HOURS = 36;

function getPacificDate(date: Date = new Date()): string {
  return formatNumericDate(date);
}

function daysBetween(dateA: string, dateB: string): number {
  const a = new Date(dateA + 'T00:00:00');
  const b = new Date(dateB + 'T00:00:00');
  return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
}

export function isStreakActive(streak: UserStreak | null): boolean {
  if (!streak || !streak.lastActiveDate) return false;
  const today = getPacificDate();
  const gap = daysBetween(streak.lastActiveDate, today);
  return gap <= 1;
}

export function isStreakInGrace(streak: UserStreak | null): boolean {
  if (!streak || !streak.lastActiveDate) return false;
  const now = new Date();
  const lastActive = new Date(streak.lastActiveDate + 'T23:59:59');
  const hoursSince = (now.getTime() - lastActive.getTime()) / (1000 * 60 * 60);
  return hoursSince > 24 && hoursSince <= GRACE_HOURS;
}

export function updateStreak(current: UserStreak | null): UserStreak {
  const today = getPacificDate();

  if (!current) {
    return { currentDays: 1, longestDays: 1, lastActiveDate: today };
  }

  if (current.lastActiveDate === today) {
    return current;
  }

  const gap = daysBetween(current.lastActiveDate, today);

  if (gap === 1) {
    const newCurrent = current.currentDays + 1;
    return {
      currentDays: newCurrent,
      longestDays: Math.max(newCurrent, current.longestDays),
      lastActiveDate: today,
    };
  }

  return {
    currentDays: 1,
    longestDays: current.longestDays,
    lastActiveDate: today,
  };
}
